import Module, { registerModuleClass } from '@sparta.global.utilities/sparta-module-utility/dist/es6.js';
import GlobalSearchCommonUtility from '@sparta.global-gt.utilities/global-search-common-utility/dist/es6.js';
import GlobalSearchJcrUtility from '@sparta.global-gt.utilities/global-search-jcr-utility/dist/es6.js';
import { circleButton } from '@sparta.global.helpers/circle-button-helper/dist/es6.js';
import { textInput } from '@sparta.global.helpers/text-inputs-helper/dist/es6.js';
import $ from '@sparta.global.vendors/jquery';
import Handlebars from '@sparta.global.vendors/handlebars';

/**
 * A class that binds data with the view and attaches all the required events to search input box.
 */
class InputController {
  /**
   * Creates an instance of InputController.
   * @param {object} options - which holds view and model for input box.
   * @memberof GlobalSearchModule
   */
  constructor(options = {}) {
    this.inputView = options.view;
    this.inputModel = options.model;
    this.moduleInstance = options.moduleInstance;
    this.container = options.container;
    this.viewport = 'large';
    const mediaQueryData = this.moduleInstance.Store.getData('currentMQ');
    //set viewport for responsive page | for non-responsive keep large
    if (mediaQueryData && document.getElementsByTagName('body')[0]?.dataset?.spartaVersion) {
      this.viewport = mediaQueryData.viewport;
    }
    this.inputUITargetClass = 'global-nav-search';
    this.inputTextboxId = '#nav-search-query';
    this.clearSearchLinkId = '#global-search-input-close-link';
    if (this.viewport.indexOf('medium') > -1 && this.moduleInstance.inputExperience.toLowerCase() === 'header') {
      this.inputTextboxId = '#nav-search-query-md';
      this.clearSearchLinkId = '#global-search-input-close-link-md';
    }
    this.inputTextboxContainerClass = '.spa-global-search-form--lg';
    this.submitButtonClass = '.submit';
    this.autocompleteContainerId = '#spa-global-search-widget-container';
    this.inputTextbox = null;
    this.inputContainer = null;
    this.submitButton = null;
    this.clearSearchLink = null;
    this.autocompleteContainer = null;
    this.onLoad = true;
    this.submitIconMd = null;
    this.allowActiveStateRender = false;
    if (this.viewport.indexOf('large') > -1 && this.moduleInstance.inputExperience.toLowerCase() === 'header') {
      this.allowActiveStateRender = true;
    }
    this.isActive = false;
  }

  /**
   * Called when this controller is bootstrapped by the module.
   * @returns {void}
   */
  init() {
    this.render();
    this.attachEvents();
    this.moduleInstance.Events.sub('global.search.medium-breakpoint', $.proxy(this.setMediumBreakpointView, this));
    this.setSmallBreakpointView();
    this.onLoad = false;
    this.processQueryString();
  }

  /**
   * Called when this module is bootstrapped by the client.
   * @param {object} jcrHelper - search JCR data.
   * @returns {void}
   */
  render() {
    const view = this.inputView.getView({
      inputExperience: this.moduleInstance.inputExperience
    });
    const data = this.inputModel.getData({
      site: this.moduleInstance.site,
      lang: this.moduleInstance.lang
    });
    if (view) {
      const target = this.container[0].getElementsByClassName(this.inputUITargetClass);
      if (target && target[0]) {
        target[0].innerHTML = view({
          data: data
        }, {
          helpers: {
            textInput,
            circleButton
          },
          partials: {}
        });
        target[0].style.display = "block";
      }
    }
    this.inputTextbox = this.container.find(this.inputTextboxId);
    this.inputContainer = this.container.find(this.inputTextboxContainerClass);
    this.submitButton = this.container.find(this.submitButtonClass);
    this.clearSearchLink = this.container.find(this.clearSearchLinkId);
    this.autocompleteContainer = document.querySelector(this.autocompleteContainerId);
    this.deactivateTextbox();
    if (this.moduleInstance.inputExperience.toLowerCase() === 'stage') {
      this.submitButton.attr('aria-label', this.moduleInstance.settings.globalSearchJcr.componentsSettings.stringResource.searchSubmit);
    }
  }

  /**
   * registers events for input box and search button.
   * @returns {void}
   */
  attachEvents() {
    if (this.inputTextbox && this.submitButton) {
      if (this.viewport.indexOf('medium') > -1 && this.moduleInstance.inputExperience.toLowerCase() === 'header' && this.submitIconMd) {
        this.submitIconMd.on('click', $.proxy(this.handleMediumBreakpointSubmitClick, this));
      }
      this.submitButton.on('click', $.proxy(this.handleSubmitButtonClick, this));
      this.inputTextbox.on('keyup click', $.proxy(this.handleChange, this));
      this.clearSearchLink.on('keyup click', $.proxy(this.handleClearSearchClick, this));
      $(document).on('mouseup', $.proxy(this.handleBodyClick, this));
      $(document).on('keyup', $.proxy(this.handleBodyKeyup, this));
    }
  }

  /**
   * Processing of query string to setup search results page.
   * @returns {void}
   */
  processQueryString() {
    if (this.moduleInstance.inputExperience.toLowerCase() === 'stage') {
      this.inputTextbox.attr('placeholder', '');
      // QueryString check & retrieval for search results page use case.
      const parser = new RegExp('[?&]state=([^&]*)');
      const result = parser.exec(window.location.search);
      if (result && result[1]) {
        let reqParam = decodeURIComponent(result[1]);
        this.inputTextbox.val(reqParam);
        this.toggleCloseLink(reqParam);
        window.setTimeout($.proxy(function () {
          this.moduleInstance.Events.pub('global.search.request-updated', {
            query: reqParam,
            target: this.autocompleteContainer,
            invoker: this.submitButton
          });
          window.history.replaceState(reqParam, '', location.pathname);
        }, this), 1000);
      }
    }
  }

  /**
   * Handles Submit button click.
   * Event subscriber must remove any forbidden characters present and
   * create complete request param using query.
   * @returns {void}
   */
  handleSubmitButtonClick(event) {
    if (this.moduleInstance.resultsExperience && this.moduleInstance.resultsExperience.toLowerCase() === 'flyout') {
      $(event.target).attr('aria-expanded', 'true');
    }
    if (this.allowActiveStateRender && this.isActive === false) {
      window.setTimeout($.proxy(this.activateTextbox, this), 200);
      return;
    }
    if (this.viewport.indexOf('medium') > -1 && this.moduleInstance.inputExperience.toLowerCase() === 'header') {
      $(this.inputTextbox).val('');
      this.toggleCloseLink('');
      this.moduleInstance.Events.pub('global.search.query-updated', {
        query: '',
        target: this.autocompleteContainer
      });
    } else {
      this.moduleInstance.Events.pub('global.search.hide-popup');
      this.moduleInstance.Events.pub('global.search.request-updated', {
        query: $.trim(this.inputTextbox.val()),
        target: this.autocompleteContainer,
        invoker: event.target
      });
    }
  }

  /**
   * Handles submit click for popup button.
   * Event subscriber must remove any forbidden characters present and
   * create complete request param using query.
   * @returns {void}
   */
  handleMediumBreakpointSubmitClick(events) {
    this.moduleInstance.Events.pub('global.search.hide-popup');
    this.moduleInstance.Events.pub('global.search.request-updated', {
      query: $.trim(this.inputTextbox.val()),
      target: this.autocompleteContainer,
      invoker: events.target
    });
  }

  /**
   * Adds show class to input container.
   * @returns {void}
   */
  showTextbox() {
    if (this.viewport.indexOf('medium') === -1) {
      this.inputContainer.addClass('show');
      this.container.find(".spa-circle-btn-label").text(this.moduleInstance.settings.globalSearchJcr.componentsSettings.stringResource.searchSubmit);
      if (this.moduleInstance.inputExperience.toLowerCase() !== 'stage') {
        this.inputTextbox.attr('placeholder', this.moduleInstance.settings.globalSearchJcr.componentsSettings.stringResource.placeholder);
      }
    }
    if (this.moduleInstance.inputExperience.toLowerCase() === 'stage') {
      this.inputContainer.addClass('show');
    }
  }

  /**
   * Handles all the keyboard and mouse events for search input box.
   * Event subscriber must verify query and remove forbidden characters before updating query.
   * @param {events} events - holds event related properties and methods.
   * @returns {void}
   */
  handleChange(events) {
    this.showTextbox();
    const tempQuery = $.trim(this.inputTextbox.val());
    this.toggleCloseLink(tempQuery);
    if (events.keyCode === 13) {
      if (this.viewport.indexOf('medium') > -1 && this.moduleInstance.inputExperience.toLowerCase() === 'header') {
        this.handleMediumBreakpointSubmitClick(events);
      } else {
        this.handleSubmitButtonClick(events);
      }
    } else if (!events.keyCode || events.keyCode && events.keyCode !== 27) {
      this.moduleInstance.Events.pub('global.search.query-updated', {
        query: tempQuery,
        target: this.autocompleteContainer
      });
    }
  }

  /**
   * Show/hides close link.
   * @param {string} inputText text present in input box.
   * @returns {void}
   */
  toggleCloseLink(inputText) {
    const className = sparta && sparta.language && sparta.language === 'es' ? 'clear-input-es' : 'clear-input';
    if (inputText.length > 0) {
      if (this.moduleInstance.inputExperience.toLowerCase() === 'stage') {
        this.clearSearchLink.addClass(className);
      } else {
        this.clearSearchLink.show();
      }
    } else {
      if (this.moduleInstance.inputExperience.toLowerCase() === 'stage') {
        this.clearSearchLink.removeClass(className);
      } else {
        this.clearSearchLink.hide();
      }
    }
  }

  /**
   * Clears textbox value  and retains the focus back to textbox.
   * @param {event} event - holds event related properties and methods.
   * @returns {void}
   */
  handleClearSearchClick(event) {
    if (event.type === 'click' || event.keyCode === 13) {
      this.inputTextbox.val('');
      this.inputContainer.removeClass('show');
      if (this.moduleInstance.inputExperience.toLowerCase() !== 'stage') {
        this.inputTextbox.attr('placeholder', this.moduleInstance.settings.globalSearchJcr.componentsSettings.stringResource.placeholder);
      }
      this.moduleInstance.Events.pub('global.search.query-updated', {
        query: '',
        target: this.autocompleteContainer
      });
      this.inputTextbox.trigger('focus');
      this.showTextbox();
      this.toggleCloseLink('');
    }
  }

  /**
   * Set input module for medium breakpoint.
   * @param {object} elmDetails target element for input control.
   * @returns {void}
   */
  setMediumBreakpointView(elmDetails) {
    if (elmDetails.data.target.children().length === 0) {
      const data = this.inputModel.getData({
        site: this.moduleInstance.site,
        lang: this.moduleInstance.lang
      });
      const inputWapper = $('#input-module-wrapper-md');
      if (inputWapper.length > 0) {
        elmDetails.data.target.append(inputWapper);
      } else {
        const searchInputMdView = moduleConfig.partials['search-ui-header-md'];
        if (data && searchInputMdView) {
          elmDetails.data.target.html(searchInputMdView({
            data: data
          }, {
            helpers: {
              textInput,
              circleButton
            },
            partials: {}
          }));
        }
        // re-initialize controls to get new rendered elements.
        this.inputTextbox = elmDetails.data.target.find(this.inputTextboxId);
        this.inputTextbox.maxLength = this.moduleInstance.jcrHelper.getSettings({
          key: 'searchInputMaxLength'
        });
        this.submitIconMd = elmDetails.data.target.find(this.submitButtonClass);
        this.submitIconMd.find('.spa-ada-text').text(this.moduleInstance.settings.globalSearchJcr.componentsSettings.stringResource.searchSubmit);
        this.clearSearchLink = elmDetails.data.target.find(this.clearSearchLinkId);
        this.clearSearchLink.hide();
        this.detachEvents();
        this.attachEvents();
      }
    }
    if (elmDetails.data.focusInput) {
      window.setTimeout($.proxy(function () {
        $(this.inputTextbox)[0].focus();
      }, this), 200);
    } else if (elmDetails.data.focusButton) {
      window.setTimeout($.proxy(function () {
        $(this.submitIconMd)[0].focus();
      }, this), 200);
    }
  }

  /**
   * Set input module for small breakpoint.
   * @returns {void}
   */
  setSmallBreakpointView() {
    if (this.viewport.indexOf('small') > -1 && this.moduleInstance.inputExperience.toLowerCase() === 'header') {
      this.inputContainer.addClass('show');
      this.inputTextbox.attr('placeholder', this.moduleInstance.settings.globalSearchJcr.componentsSettings.stringResource.placeholder);
      this.toggleCloseLink('');
      this.inputContainer.find('.spa-circle-btn-label').text(this.moduleInstance.settings.globalSearchJcr.componentsSettings.stringResource.searchSubmit);
    }
  }

  /**
   * Detach all input control events.
   * @returns {void}
   */
  detachEvents() {
    if (this.inputTextbox && this.clearSearchLink) {
      this.inputTextbox.off('keyup click');
      this.clearSearchLink.off('click');
    }
  }

  /**
   * Handles submit button focus
   * @param {event} event param to be used to get target element.
   * @returns {void}
   */
  handleSubmitClearFocus(event) {
    if (this.moduleInstance.resultsExperience && this.moduleInstance.resultsExperience.toLowerCase() === 'flyout') {
      $(event.target).attr('aria-expanded', 'false');
    }
  }

  /**
   * Handles body click.
   * @param {event} event param to be used to get target element.
   * @returns {void}
   */
  handleBodyClick(event) {
    if (event.target && event.target.tagName.toLowerCase() !== 'html' && event.target.tagName.toLowerCase() !== 'body' && this.checkIfExternalClick(event.target)) {
      this.deactivateTextbox();
      this.moduleInstance.Events.pub('global.search.focus-out');
    }
  }

  /**
   * Handles body keyup.
   * @param {event} event param to be used to get target element.
   * @returns {void}
   */
  handleBodyKeyup(event) {
    if (event.keyCode && event.keyCode === 27) {
      this.moduleInstance.Events.pub('global.search.escape-clicked');
    }
  }

  /**
   * check for external click outside popup.
   * @param {event} element element to be checked for external click.
   * @returns {void}
   */
  checkIfExternalClick(element) {
    if ($(element).parents(this.autocompleteContainerId).length === 0) {
      return true;
    }
    return false;
  }

  /**
   * deactivate textbox .
   * @returns {void}
   */
  deactivateTextbox() {
    if (this.allowActiveStateRender === true) {
      this.container.find(".spa-circle-btn-label").text(this.moduleInstance.settings.globalSearchJcr.componentsSettings.stringResource.search);
      $(this.inputTextbox).css('visibility', 'hidden');
      $(this.inputTextbox).val('');
      this.inputContainer.removeClass('show');
      this.isActive = false;
    }
  }

  /**
   * activate textbox .
   * @returns {void}
   */
  activateTextbox() {
    if (this.allowActiveStateRender === true) {
      this.inputTextbox.css('visibility', '');
      this.showTextbox();
      const tempQuery = $.trim(this.inputTextbox.val());
      this.toggleCloseLink(tempQuery);
      this.moduleInstance.Events.pub('global.search.query-updated', {
        query: tempQuery,
        target: this.autocompleteContainer
      });
      this.isActive = true;
      $(this.inputTextbox).focus();
    }
  }
}

/**
 * A class to get InputView
 */
class InputView {
  /**
  * Creates an instance of InputView.
  * @param {object} options - template details.
  * @memberof GlobalSearchModule
  */
  constructor(options) {
    this.templateId = options.settings.templateId;
    this.moduleInstance = options.moduleInstance;
  }

  /**
  * Called to get apllicable view
  * @returns {void}
  */
  getView() {
    return moduleConfig.partials[this.templateId];
  }
}

/**
 * Model class to get the data from service and massage the data.
 */
class InputModel {
  /**
  * Creates an instance of InputModel.
  * @param {object} options - instance settings to be initialzed with.
  * @memberof GlobalSearchModule
  */
  constructor(options) {
    this.settings = options.settings;
    this.moduleInstance = options.moduleInstance;
    this.viewport = 'large';
    const mediaQueryData = this.moduleInstance.Store.getData('currentMQ');
    //set viewport for responsive page | for non-responsive keep large
    if (mediaQueryData && document.getElementsByTagName('body')[0]?.dataset?.spartaVersion) {
      this.viewport = mediaQueryData.viewport;
    }
  }

  /**
  * Called when this module is bootstrapped by the client.
  * @returns {object} - JCR data
  */
  getData() {
    let searchData = this.moduleInstance.jcrHelper.getSearchData();
    const label = this.moduleInstance.settings.globalSearchJcr.componentsSettings.stringResource;
    if (this.moduleInstance.inputExperience.toLowerCase() === 'stage') {
      searchData.searchInputProperties.lg.label.text = label.howCanWeHelp;
      searchData.searchInputProperties.lg.label.adaText = '';
    }
    searchData = $.extend(searchData, label);
    const config = {
      isLargeViewport: this.viewport.indexOf('large') > -1,
      isInputExperienceHeader: this.moduleInstance.inputExperience.toLowerCase() === 'header'
    };
    searchData = $.extend(searchData, {
      config: config
    });
    return searchData;
  }
}

const Template$3 = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<div class=\"global-nav-search\" style=\"display: none;\"></div>";
},"useData":true});
const renderTemplate$3 = function (data, options) {
  return Template$3(data, options);
};

const Template$2 = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div id=\"input-module-wrapper-md\" class=\"spa-global-search--flyout-search\">\r\n  "
    + container.escapeExpression((lookupProperty(helpers,"textInput")||(depth0 && lookupProperty(depth0,"textInput"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"searchInputProperties") : stack1)) != null ? lookupProperty(stack1,"md") : stack1),{"name":"textInput","hash":{},"data":data,"loc":{"start":{"line":2,"column":2},"end":{"line":2,"column":45}}}))
    + "\r\n  <div class=\"show-for-medium-only\">\r\n    <div class=\"spa-icon-wrapper\">\r\n      <span id=\"global-search-input-close-link-md\" class=\"spa-icon icon-close\" tabindex=\"0\" role=\"button\">\r\n        <span class=\"ada-hidden\">"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"clearQuery") : stack1), depth0))
    + "</span>\r\n      </span>\r\n      <span class=\"spa-divider\"></span>\r\n      "
    + container.escapeExpression((lookupProperty(helpers,"circleButton")||(depth0 && lookupProperty(depth0,"circleButton"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"searchButtonProperties") : stack1)) != null ? lookupProperty(stack1,"md") : stack1),{"name":"circleButton","hash":{},"data":data,"loc":{"start":{"line":9,"column":6},"end":{"line":9,"column":53}}}))
    + "\r\n    </div>\r\n  </div>\r\n</div>";
},"useData":true});
const renderTemplate$2 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$2(data, options);
};

const Template$1 = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div id=\"spa-global-search-widget\">\r\n  <div id=\"spa-global-search-widget-container\" class=\"spa-global-search\">\r\n    <div class=\"spa-global-search-form\">\r\n      <div class=\"spa-global-search-form--lg hide-for-medium-only\">\r\n        "
    + container.escapeExpression((lookupProperty(helpers,"textInput")||(depth0 && lookupProperty(depth0,"textInput"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"searchInputProperties") : stack1)) != null ? lookupProperty(stack1,"lg") : stack1),{"name":"textInput","hash":{},"data":data,"loc":{"start":{"line":5,"column":8},"end":{"line":5,"column":51}}}))
    + "\r\n        <div class=\"spa-icon-wrapper\">\r\n          <span id=\"global-search-input-close-link\" class=\"spa-icon icon-close\" tabindex=\"0\" role=\"button\">\r\n            <span class=\"ada-hidden\">"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"clearQuery") : stack1), depth0))
    + "</span>\r\n          </span>\r\n          <span class=\"spa-divider\"></span>\r\n          "
    + container.escapeExpression((lookupProperty(helpers,"circleButton")||(depth0 && lookupProperty(depth0,"circleButton"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"searchButtonProperties") : stack1)) != null ? lookupProperty(stack1,"lg") : stack1),{"name":"circleButton","hash":{},"data":data,"loc":{"start":{"line":11,"column":10},"end":{"line":11,"column":57}}}))
    + "\r\n        </div>\r\n      </div>\r\n      <div class=\"spa-global-search-form--md show-for-medium-only\">\r\n        "
    + container.escapeExpression((lookupProperty(helpers,"circleButton")||(depth0 && lookupProperty(depth0,"circleButton"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"searchButtonProperties") : stack1)) != null ? lookupProperty(stack1,"md") : stack1),{"name":"circleButton","hash":{},"data":data,"loc":{"start":{"line":15,"column":8},"end":{"line":15,"column":55}}}))
    + "\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>";
},"useData":true});
const renderTemplate$1 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$1(data, options);
};

const Template = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div id=\"spa-global-search-widget-container\" class=\"spa-global-search spa-global-search--page\">\r\n  <div class=\"spa-global-search-form\">\r\n    <div class=\"spa-global-search-form--lg\">\r\n      "
    + container.escapeExpression((lookupProperty(helpers,"textInput")||(depth0 && lookupProperty(depth0,"textInput"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"searchInputProperties") : stack1)) != null ? lookupProperty(stack1,"lg") : stack1),{"name":"textInput","hash":{},"data":data,"loc":{"start":{"line":4,"column":6},"end":{"line":4,"column":49}}}))
    + "\r\n      <span id=\"global-search-input-close-link\" class=\"spa-icon icon-close\" tabindex=\"0\" role=\"button\">\r\n        <span class=\"ada-hidden\">"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"clearQuery") : stack1), depth0))
    + "</span>\r\n      </span>\r\n      <button class=\"spa-btn spa-btn--secondary spa-btn--medium search-btn submit track-me\" type=\"button\"\r\n        data-bactmln=\"search-button-stage\" name=\"search-button-stage\">\r\n        "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"search") : stack1), depth0))
    + "\r\n      </button>\r\n    </div>\r\n  </div>\r\n</div>";
},"useData":true});
const renderTemplate = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template(data, options);
};

// Import templates & helpers
var _moduleConfig = {
  "name": "@sparta.global-gt.modules/global-search-input-module",
  "version": "1.0.15",
  "id": "globalSearchInputModule",
  "containerID": "globalGtGlobalSearchInputModule",
  "moduleName": "global-gt-global-search-input-module",
  "semantic": true,
  "superModule": false,
  "viewPort": ["small", "medium", "large"],
  "tag": "div",
  "nameSpace": "global-search-input-module",
  "data": {
    "init": "GlobalSearchInputModule",
    "options": {
      "globalSearchJcr": "{{json globalSearchJcr}}"
    }
  },
  "moduleParams": [{
    "name": "isAuth",
    "type": "boolean",
    "description": "This parameter controls the rendering of module in authenticated vs unauthenticated mode. The default is false.",
    "default": false,
    "values": [true, false]
  }],
  "layout": "layout",
  "render": function (context) {
    return renderTemplate$3(context, {
      "helpers": {
        circleButton,
        textInput
      },
      "partials": {
        "search-ui-header-md": renderTemplate$2,
        "search-ui-header": renderTemplate$1,
        "search-ui-stage": renderTemplate
      }
    });
  },
  "template": renderTemplate$3,
  "partials": {
    "search-ui-header-md": renderTemplate$2,
    "search-ui-header": renderTemplate$1,
    "search-ui-stage": renderTemplate
  },
  "helpers": {
    circleButton,
    textInput
  },
  "dependencies": {
    "@sparta.global.utilities/sparta-module-utility": "^1.0.0",
    "@sparta.global.utilities/sparta-style-token-utility": "^1.0.0",
    "@sparta.global-gt.utilities/global-search-common-utility": "^1.0.0",
    "@sparta.global-gt.utilities/global-search-jcr-utility": "^1.0.0",
    "@sparta.global.helpers/circle-button-helper": "^1.0.0",
    "@sparta.global.helpers/text-inputs-helper": "^1.0.0",
    "@sparta.global.vendors/jquery": "^3.7.1"
  },
  "stores": []
};

/**
 * Implementation of the global-search-input-module module.
 * @copyright 2021-7-26
 * @since 7/26/2021, 6:13:04 AM
 * @author Dixit, Rahul
 * @file
 */

/* global Module */

/**
 * Search pub sub events
 */
const SEARCH_EVENTS = ['global.search.escape-clicked', 'global.search.hide-popup', 'global.search.medium-breakpoint', 'global.search.query-updated', 'global.search.request-updated', 'global.search.results-received'];

/**
 * The GlobalSearchInputModule Sparta Module.
 * Module responsible for rendering input textbox and search icon based on input experience
 * @export
 * @class
 */
class GlobalSearchInputModule extends Module {
  /**
   * Creates an instance of GlobalSearchInputModule.
   * @param {object} options - used to initialize module.
   * @memberof GlobalSearchInputModule
   */
  constructor(options, context) {
    super({
      ...options,
      context,
      config: _moduleConfig
    });
    this.site = 'OLB';
    this.lang = 'en';
    this.inputExperience = 'header';
    this.resultsExperience = 'flyout';
    this.resultsContainerId = '';
    this.imports = {
      InputController,
      InputView,
      InputModel
    };
    this.isAuth = false;
    this.jcrHelper = null;
    this.Widget = window.spaWidgetParams[this.applicationContext.name][this.applicationContext.version][this.applicationContext.id];
  }

  /**
   * Called when this module is bootstrapped by the client.
   * @returns {void}
   */
  init() {
    this.unsubscribeAllEvents();
    this.site = GlobalSearchCommonUtility.site.getCurrentSite();
    this.lang = GlobalSearchCommonUtility.lang.getPageLanguage();
    this.isAuth = this.params && this.params.isAuth;
    const widgetOptionsStr = JSON.parse(this.applicationContainer.getAttribute('data-sparta-options'));
    if (widgetOptionsStr.inputExperience) {
      this.inputExperience = widgetOptionsStr.inputExperience;
    }
    if (widgetOptionsStr.resultsExperience) {
      this.resultsExperience = widgetOptionsStr.resultsExperience;
    }
    this.jcrHelper = new GlobalSearchJcrUtility({
      site: this.site,
      searchJcrData: this.settings.globalSearchJcr,
      moduleInstance: this
    });
    const componentsSetting = this.jcrHelper.getComponentsSetting({
      inputExperience: this.inputExperience,
      resultsExperience: this.resultsExperience,
      componentName: 'inputUI'
    });
    const viewType = componentsSetting.view.type;
    const view = new this.imports[viewType]({
      settings: componentsSetting.view.attributes,
      moduleInstance: this
    });
    const modelType = componentsSetting.model.type;
    const model = new this.imports[modelType]({
      settings: componentsSetting.model.dataServices,
      moduleInstance: this
    });
    const controllerType = componentsSetting.controller.type;
    const controller = new this.imports[controllerType]({
      view,
      model,
      container: this.settings.$target,
      moduleInstance: this
    });
    controller.init();
  }

  /**
   * Unsubscribe all search events preventing duplicates
   * @returns {void}
   */
  unsubscribeAllEvents() {
    SEARCH_EVENTS.forEach(searchEvent => {
      this.Events.unsubByEvent(searchEvent);
    });
  }
}
registerModuleClass({
  moduleClass: GlobalSearchInputModule,
  moduleConfig: _moduleConfig
});
const moduleConfig = _moduleConfig;

export { GlobalSearchInputModule as default, moduleConfig };
